import React, { useState, useEffect, useRef } from "react";
import axios from "axios";
import I404Png from "./../images/404Basil.png";
import {
  Disclosure,
  DisclosureButton,
  DisclosurePanel,
} from "@headlessui/react";
import { FaAngleDown, FaAngleUp } from "react-icons/fa";
import MenuScheduleFilter from "./MenuScheduleFilter";
import { HiOutlineCog } from "react-icons/hi";
import { Drawer } from "@mui/material";
import SidePanelContent from "./SidePanelContent";
import NcImage from "shared/NcImage/NcImage";

export interface menuSchedule {
  menuDay: string;
  fromTime: any;
  toTime: any;
}
interface Category {
  id: number;
  name: string;
  isDeleted: boolean;
  createdBy: number;
  createdDate: string;
  modifiedBy?: number;
  modifiedDate?: string;
  items?: Item[];
}
interface Item {
  name: string;
  price: number;
}
interface MerchantStore {
  logoUrl?: string;
  orderLeadTime?: number;
  image?: string;
  title?: string;
  customTextOne?: string;
  pauseOnlineOrder?: boolean;
  pauseOnlineOrderLabel?: string;
  onlineDeliveryId?: string;
  isDelivery?: boolean;
  subTitle?: string;
}
interface ResponseData {
  merchantStore: MerchantStore;
  all: any[];
  menus: any[];
}

const API_BASE = `https://basil-backoffice-staging.com/api`;

// Guard against a cyclic child-modifier-set graph coming back from the API
const MAX_MODIFIER_DEPTH = 5;

const QRMenu: React.FC = () => {
  const [categories, setCategories] = useState<Category[]>([]);
  const [modalOpen, setModalOpen] = useState<boolean>(false);
  const [menuScheduleData, setMenuScheduleData] = useState<menuSchedule[]>([]);
  const [menus, setMenus] = useState<any[]>([]);
  const [selectedMenu, setSelectedMenu] = useState<any | null>(null);
  const [selectedSchedule, setSelectedSchedule] = useState<
    menuSchedule[] | null
  >(null);
  const [selectedCategoryId, setSelectedCategoryId] = useState<number | null>(
    null
  );
  const [isOpen, setIsOpen] = useState(false);
  const [isHamburgerOpen, setIsHamburgerOpen] = useState(false);
  const [hasError, setHasError] = useState(false);
  const [isopen, setIsopen] = useState(false);
  const [popUpContent, setPopUpContent] = useState<any>();
  // Child modifier sets are fetched lazily per modifier item and cached by its id
  const [childModifierSets, setChildModifierSets] = useState<
    Record<number, any[]>
  >({});
  const [loadingChildSets, setLoadingChildSets] = useState<
    Record<number, boolean>
  >({});
  const [childSetErrors, setChildSetErrors] = useState<Record<number, boolean>>(
    {}
  );
  const inFlightChildSets = useRef<Set<number>>(new Set());
  const [onlineOrderSettings, setOnlineOrderSettings] =
    useState<MerchantStore | null>(null);
  const merchantName = window.location.pathname.split("/")[1];
  useEffect(() => {
    const fetchData = async () => {
      try {
        const apiUrl = `/menu/qr`;
        const response = await axios.get(
          API_BASE + apiUrl,

          {
            headers: {
              "Content-Type": "application/json",
              MID: merchantName,
            },
          }
        );
      
        const data = response.data as ResponseData;
        const menusData = data?.menus || [];
        setMenus(menusData);
        if (menusData.length > 0) {
          setSelectedMenu(menusData[0]);
        }

        setMenuScheduleData(
          response?.data?.merchantStore?.storeHours as menuSchedule[]
        );
        const merchantStore = data?.merchantStore;
        const itemData =
          data?.all !== null ? data?.all : data?.menus[0].categories;
        if (itemData.length === 0) {
          setHasError(true);
        } else {
          const filteredCategories = itemData?.filter(
            (category: { items: any[] }) =>
              category?.items && category?.items?.length > 0
          );
          const categoriesWithSortedItems = filteredCategories.map(
            (category: any) => ({
              ...category,
              items: sortItemsAlphabetically(category.items || []),
            })
          );
          setCategories(categoriesWithSortedItems);
          if (filteredCategories.length > 0) {
            setSelectedCategoryId(filteredCategories[0].id);
          }
          setOnlineOrderSettings({
            ...merchantStore,
            customTextOne: merchantStore?.subTitle ?? "",
          });
          if (merchantStore?.logoUrl) {
            localStorage.setItem(
              "onlineOrderSettingsLogo",
              merchantStore.logoUrl
            );
          }
        }
      } catch (error) {
        setHasError(true);
        console.error("Error fetching online order settings:", error);
      }
    };
    fetchData();
  }, []);
 
  // A modifier row arrives either wrapped (/menu/qr gives
  // modifierSetModifierItems[].modifierItem) or flat (the child-modifier-sets
  // endpoint returns the modifier item itself) - read both the same way.
  const toModifierItem = (row: any) => {
    const source = row?.modifierItem ?? row;
    return {
      id: source?.id,
      name: source?.name,
      price: source?.price,
      hasChildModifierSets: Boolean(
        source?.hasChildModifierSets ?? row?.hasChildModifierSets
      ),
    };
  };

  // child-modifier-sets returns modifier sets whose modifier items sit under
  // `items`; /menu/qr calls the same collection modifierSetModifierItems. Line
  // them up on one key so a set from either source renders identically.
  const normalizeChildModifierSets = (payload: any): any[] => {
    if (!Array.isArray(payload)) return [];
    return payload.map((set: any) => ({
      ...set,
      modifierSetModifierItems:
        set?.modifierSetModifierItems ?? set?.items ?? [],
    }));
  };

  const fetchChildModifierSets = async (modifierItemId: number) => {
    // Already cached or in flight - nothing to do (also covers collapse/expand)
    if (
      childModifierSets[modifierItemId] !== undefined ||
      inFlightChildSets.current.has(modifierItemId)
    ) {
      return;
    }
    inFlightChildSets.current.add(modifierItemId);
    setLoadingChildSets((prev) => ({ ...prev, [modifierItemId]: true }));
    setChildSetErrors((prev) => ({ ...prev, [modifierItemId]: false }));
    try {
      const response = await axios.get(
        `${API_BASE}/onlineOrder/menu/modifier-item/${modifierItemId}/child-modifier-sets`,
        {
          headers: {
            "Content-Type": "application/json",
            MID: merchantName,
          },
        }
      );
      setChildModifierSets((prev) => ({
        ...prev,
        [modifierItemId]: normalizeChildModifierSets(response?.data),
      }));
    } catch (error) {
      console.error(
        `Error fetching child modifier sets for modifier item ${modifierItemId}:`,
        error
      );
      setChildSetErrors((prev) => ({ ...prev, [modifierItemId]: true }));
    } finally {
      inFlightChildSets.current.delete(modifierItemId);
      setLoadingChildSets((prev) => ({ ...prev, [modifierItemId]: false }));
    }
  };

  // Sort modifier sets: by orderBy if valid, otherwise alphabetically
  const sortModifierSets = (sets: any[]) =>
    sets.slice().sort((a: any, b: any) => {
      const orderByA = a.orderBy;
      const orderByB = b.orderBy;
      
      // Check if orderBy is valid (not null, not undefined, not empty string, not 999)
      const isValidOrderByA = orderByA != null && orderByA !== '' && orderByA !== 999;
      const isValidOrderByB = orderByB != null && orderByB !== '' && orderByB !== 999;
      
      // If both have valid orderBy, sort by orderBy first
      if (isValidOrderByA && isValidOrderByB) {
        if (orderByA !== orderByB) {
          return orderByA - orderByB;
        }
        // If orderBy values are the same, sort alphabetically
        return (a.name || '').localeCompare(b.name || '');
      }
      
      // If only one has valid orderBy, prioritize it
      if (isValidOrderByA && !isValidOrderByB) return -1;
      if (!isValidOrderByA && isValidOrderByB) return 1;
      
      // If neither has valid orderBy, sort alphabetically
      return (a.name || '').localeCompare(b.name || '');
    });

  const sortModifierSetItems = (rows: any[]) =>
    (rows || [])
      .slice()
      .sort((a: any, b: any) => (a.orderBy ?? 0) - (b.orderBy ?? 0)); // ascending
    
  const renderModifierItemRow = (
    row: any,
    keyPrefix: string,
    depth: number
  ) => {
    const item = toModifierItem(row);
    const rowKey = `${keyPrefix}-item${row?.id ?? item.id}`;

    // Leaf modifier - flat row, unchanged
    if (!item.hasChildModifierSets || item.id == null) {
      return (
        <div
          key={rowKey}
          className="p-4 pt-3 last:pb-0 text-slate-600 text-sm dark:text-slate-300 leading-6 flex items-center justify-between"
        >
          <div className="flex justify-between w-full">
            <span>{item.name}</span>
            <span>${item.price}</span>
          </div>
        </div>
      );
    }

    // hasChildModifierSets - expandable, children fetched on first open
    return (
      <Disclosure key={rowKey}>
        {({ open }) => (
          <>
            <DisclosureButton
              onClick={() => fetchChildModifierSets(item.id)}
              className="flex items-center justify-between w-full px-4 py-2 text-sm text-slate-600 dark:text-slate-300 hover:bg-slate-100/60 dark:hover:bg-slate-700 rounded-lg focus:outline-none focus-visible:ring focus-visible:ring-slate-500 focus-visible:ring-opacity-75"
            >
              <span className="flex justify-between w-full pr-2">
                <span>{item.name}</span>
                <span>${item.price}</span>
              </span>
              {!open ? (
                <FaAngleDown className="w-4 h-4 flex-shrink-0 text-slate-600 dark:text-slate-400" />
              ) : (
                <FaAngleUp className="w-4 h-4 flex-shrink-0 text-slate-600 dark:text-slate-400" />
              )}
            </DisclosureButton>
            <DisclosurePanel className="ml-4 pl-1 border-l border-slate-200 dark:border-slate-600">
              {loadingChildSets[item.id] && (
                <p className="px-4 py-2 text-sm text-slate-500 dark:text-slate-400">
                  Loading options...
                </p>
              )}
              {childSetErrors[item.id] && (
                <p className="px-4 py-2 text-sm text-red-500">
                  Options unavailable right now.
                </p>
              )}
              {childModifierSets[item.id]?.length === 0 && (
                <p className="px-4 py-2 text-sm text-slate-500 dark:text-slate-400">
                  No options available.
                </p>
              )}
              {renderModifierSets(
                childModifierSets[item.id] || [],
                depth + 1,
                rowKey
              )}
            </DisclosurePanel>
          </>
        )}
      </Disclosure>
    );
  };

  const renderModifierSets = (
    sets: any[],
    depth: number = 0,
    keyPrefix: string = "root"
  ): React.ReactNode => {
    if (!Array.isArray(sets) || sets.length === 0) return null;
    // Stop recursing if the child-set graph ever comes back cyclic
    if (depth > MAX_MODIFIER_DEPTH) return null;

    return sortModifierSets(sets).map((modifier: any, index: number) => {
      const setKey = `${keyPrefix}-set${modifier?.id ?? index}`;
      const hasMaximum =
        typeof modifier?.maximum === "number" && modifier.maximum > 0;

      return (
        <Disclosure key={setKey}>
          {({ open }) => (
            <>
              <DisclosureButton className="flex items-center justify-between w-full px-4 py-2 font-medium text-right bg-slate-100/80 hover:bg-slate-200/60 dark:bg-slate-800 dark:hover:bg-slate-700 rounded-lg focus:outline-none focus-visible:ring focus-visible:ring-slate-500 focus-visible:ring-opacity-75 ">
                <span>
                  {modifier.name}
                  {modifier.noOfRequired > 0 && (
                    <span className="text-sm font-normal ml-auto">
                      (required {modifier.noOfRequired})
                    </span>
                  )}
                </span>
                {modifier.noOfRequired > 0 && hasMaximum && (
                  <span className="text-sm font-normal ml-auto">
                    {" "}
                    upto {modifier.maximum}{" "}
                  </span>
                )}
                {!modifier.noOfRequired && hasMaximum && (
                  <span className="text-sm font-normal ml-auto">
                    optional, upto {modifier.maximum}
                  </span>
                )}
                {!open ? (
                  <FaAngleDown className="w-4 h-4 text-slate-600 dark:text-slate-400" />
                ) : (
                  <FaAngleUp className="w-4 h-4 text-slate-600 dark:text-slate-400" />
                )}
              </DisclosureButton>
              <DisclosurePanel>
                {sortModifierSetItems(
                  modifier?.modifierSetModifierItems
                ).map((row: any) => renderModifierItemRow(row, setKey, depth))}
              </DisclosurePanel>
            </>
          )}
        </Disclosure>
      );
    });
  };

  const modifierSetsContent = () =>
    renderModifierSets(
      popUpContent?.modifierSets || [],
      0,
      `popup${popUpContent?.id ?? ""}`
    );

  const handlePopup = (data: any) => {
    setIsopen(true);
    setPopUpContent(data);
  };

  useEffect(() => {
    if (categories.length > 0 && selectedCategoryId === null) {
      const firstCategoryId = categories[0].id;
      setSelectedCategoryId(firstCategoryId); // Set the default category on page load
    }
  }, [categories]);

  const handlemenuSchedule = () => {
    if (menuScheduleData) {
      setSelectedSchedule(menuScheduleData);
      setModalOpen(true);
    }
  };

  const isOpenNow = (fromTime: string, toTime: string): boolean => {
    if (!fromTime || !toTime) return false;

    const now = new Date();
    const [fromHours, fromMinutes] = fromTime.split(":").map(Number);
    const [toHours, toMinutes] = toTime.split(":").map(Number);

    const fromDate = new Date(now);
    fromDate.setHours(fromHours, fromMinutes, 0, 0);

    const toDate = new Date(now);
    toDate.setHours(toHours, toMinutes, 0, 0);

    // Handle overnight shift (e.g., 22:00 to 02:00)
    if (toDate <= fromDate) {
      toDate.setDate(toDate.getDate() + 1);
    }

    return now >= fromDate && now <= toDate;
  };

  const getCurrentDay = (): string => {
    const daysOfWeek = [
      "Sunday",
      "Monday",
      "Tuesday",
      "Wednesday",
      "Thursday",
      "Friday",
      "Saturday",
    ];
    const currentDayIndex = new Date().getDay();
    return daysOfWeek[currentDayIndex];
  };

  const currentStatus = () => {
    const currentDay = getCurrentDay();
    if (!menuScheduleData) return { text: "Closed", color: "red" };

    const todaySchedules = menuScheduleData.filter(
      (schedule) => schedule.menuDay === currentDay
    );

    const isCurrentlyOpen = todaySchedules.some((schedule) =>
      isOpenNow(schedule.fromTime, schedule.toTime)
    );

    return {
      text: isCurrentlyOpen ? "Open" : "Closed",
      color: isCurrentlyOpen ? "#7BB542" : "red",
    };
  };

  const status = currentStatus();

  // Function to sort items by first letter or number
  const sortItemsAlphabetically = (items: any[]) => {
    return items.slice().sort((a, b) => {
      const nameA = (a.name || '').trim().toLowerCase();
      const nameB = (b.name || '').trim().toLowerCase();
      
      // Get first character of each name
      const firstCharA = nameA.charAt(0);
      const firstCharB = nameB.charAt(0);
      
      // Check if first character is a number
      const isNumberA = /^[0-9]/.test(firstCharA);
      const isNumberB = /^[0-9]/.test(firstCharB);
      
      // Numbers come before letters
      if (isNumberA && !isNumberB) return -1;
      if (!isNumberA && isNumberB) return 1;
      
      // If both are numbers or both are letters, sort normally
      return nameA.localeCompare(nameB);
    });
  };

  // Every category renders as its own section. These let the tab strip follow
  // the scroll position, and let a tab tap scroll to its section.
  const scrollContainerRef = useRef<HTMLDivElement | null>(null);
  const stickyBarRef = useRef<HTMLDivElement | null>(null);
  const tabStripRef = useRef<HTMLDivElement | null>(null);
  const sectionRefs = useRef<Record<number, HTMLDivElement | null>>({});
  const tabRefs = useRef<Record<number, HTMLDivElement | null>>({});
  // True while a tab-triggered smooth scroll runs, so the spy doesn't fight it
  const isTabScrolling = useRef(false);

  const visibleCategories = categories.filter(
    (category) => (category.items?.length ?? 0) > 0
  );

  const scrollToCategory = (categoryId: number) => {
    setSelectedCategoryId(categoryId);

    const section = sectionRefs.current[categoryId];
    const container = scrollContainerRef.current;
    if (!section || !container) return;

    isTabScrolling.current = true;
    const offset =
      section.getBoundingClientRect().top -
      container.getBoundingClientRect().top;
    container.scrollTo({
      top:
        container.scrollTop +
        offset -
        (stickyBarRef.current?.offsetHeight ?? 0),
      behavior: "smooth",
    });
    window.setTimeout(() => {
      isTabScrolling.current = false;
    }, 700);
  };

  useEffect(() => {
    const container = scrollContainerRef.current;
    const sections = categories.filter(
      (category) => (category.items?.length ?? 0) > 0
    );
    if (!container || sections.length === 0) return;

    let frame = 0;
    const syncActiveCategory = () => {
      frame = 0;
      if (isTabScrolling.current) return;

      // A section becomes current once its heading passes under the tab strip
      const line =
        container.getBoundingClientRect().top +
        (stickyBarRef.current?.offsetHeight ?? 0) +
        8;

      let activeId = sections[0].id;
      sections.forEach((category) => {
        const section = sectionRefs.current[category.id];
        if (section && section.getBoundingClientRect().top <= line) {
          activeId = category.id;
        }
      });

      // The final section can be too short to ever reach that line
      if (
        container.scrollTop + container.clientHeight >=
        container.scrollHeight - 8
      ) {
        activeId = sections[sections.length - 1].id;
      }

      setSelectedCategoryId(activeId);
    };

    const onScroll = () => {
      if (frame) return;
      frame = requestAnimationFrame(syncActiveCategory);
    };

    syncActiveCategory();
    container.addEventListener("scroll", onScroll, { passive: true });
    return () => {
      container.removeEventListener("scroll", onScroll);
      if (frame) cancelAnimationFrame(frame);
    };
  }, [categories]);

  // Keep the active tab in view within the horizontal strip
  useEffect(() => {
    if (selectedCategoryId === null) return;
    const tab = tabRefs.current[selectedCategoryId];
    const strip = tabStripRef.current;
    if (!tab || !strip) return;

    const tabRect = tab.getBoundingClientRect();
    const stripRect = strip.getBoundingClientRect();
    if (tabRect.left < stripRect.left || tabRect.right > stripRect.right) {
      strip.scrollTo({
        left: strip.scrollLeft + (tabRect.left - stripRect.left) - 24,
        behavior: "smooth",
      });
    }
  }, [selectedCategoryId]);
  useEffect(() => {
    document.body.style.overflow = isopen ? "hidden" : "auto";
    return () => {
      document.body.style.overflow = "auto";
    };
  }, [isopen]);
 
  return (
    <>
      {hasError === true ? (
        <div className="nc-Page404 fixed inset-0 flex items-center justify-center bg-white dark:bg-neutral-900 overflow-hidden z-50">
          <div className="text-center px-4 max-w-md w-full">
            <img src={I404Png} className="mx-auto mb-4 max-w-xs" alt="404 Not Found" />
            <span className="block text-lg text-neutral-800 dark:text-neutral-200 tracking-wider font-bold">
              The business you are looking for was not found. Please check the
              link and try again.
            </span>
            <div className="pt-8">

            </div>
          </div>
        </div>
      ) : (
        <>


          <div className="fixed inset-0 flex flex-col bg-white dark:bg-neutral-900 z-50">

            <div className="nc-HeaderLogged sticky top-0 z-40 ">
              <div className="nc-MainNav2Logged relative z-10 bg-white dark:bg-neutral-900 border-b border-slate-100 dark:border-slate-700">
                <div className="container">
                  <div className="h-20 flex justify-between">
                    <div className="flex items-center justify-start">
                        
                      <a
                        className="ttnc-logo inline-block text-slate-600"
                        href={`/${merchantName}`}

                      >
                        {onlineOrderSettings?.logoUrl && (
                          <img
                            src={onlineOrderSettings.logoUrl}
                            alt="Logo"
                            className="block h-10 sm:h-14 max-h-14 dark:text-gray-100 rounded-lg"
                          />
                        )}
                      </a>
                    </div>
                    <div className="flex-[2] hidden lg:flex justify-center mx-4"></div>
                    <div className="flex-1 flex items-center justify-end text-slate-700 dark:text-slate-100">
                      <div className="lg:flex">
                        <div
                          className="text-2xl"
                          onClick={() => setIsOpen(true)}
                        >
                          <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-full text-slate-700 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800 focus:outline-none flex items-center justify-center cursor-pointer">
                            <HiOutlineCog />
                          </div>
                        </div>
                        <Drawer
                          anchor="right"
                          open={isOpen}
                          sx={{ "& .MuiDrawer-paper": { width: "300px" } }}
                        >
                          <SidePanelContent setIsOpen={setIsOpen} />
                        </Drawer>
                      </div>
                    </div>

                    <div className="flex lg:hidden sm:flex sm:items-center lg:flex-1  justify-center mx-auto my-4">

                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div
              ref={scrollContainerRef}
              className="main-content flex-grow overflow-y-auto pb-10"
            >
              <div className="nc-PageHome relative">
                <div className="container relative space-y-10 my-6 lg:space-y-10 lg:my-10">
                  <div className="nc-PageHomeMenu relative">
                    <div className="flex flex-col relative mb-12">
                      {/* Title and Subtitle Section */}
                      <div className="text-center mb-6">
                        {onlineOrderSettings?.title && (
                          <h1 className="text-3xl sm:text-4xl font-bold text-gray-800 dark:text-gray-200 mb-2">
                            {onlineOrderSettings.title}
                          </h1>
                        )}
                        {onlineOrderSettings?.customTextOne && (
                          <p className="text-lg sm:text-xl text-gray-600 dark:text-gray-400 max-w-2xl mx-auto">
                            {onlineOrderSettings.customTextOne}
                          </p>
                        )}
                      </div>

                      <div className="flex items-center space-x-2 mt-4 mb-2">
                        <h3
                          className="px-3 py-1 rounded-lg"
                          style={{
                            backgroundColor: status?.color,
                          }}
                        >
                          {status?.text}
                        </h3>
                        <button
                          onClick={handlemenuSchedule}
                          className="bg-grey-500 dark:text-gray-100 px-3 py-1 rounded-lg focus:outline-black focus:ring focus:ring-grey"
                        >
                          Hours
                        </button>
                      </div>
                    </div>
                  </div>
                  <div
                    ref={stickyBarRef}
                    className="sticky top-0 z-30 bg-white dark:bg-neutral-900 py-2"
                  >
                      <div className="flex flex-col lg:flex-row lg:items-center justify-between space-y-6 lg:space-y-0 lg:space-x-2">
                        <div
                          ref={tabStripRef}
                          className="overflow-x-auto whitespace-nowrap flex py-2 items-center relative"
                        >
                        
                     {menus?.length > 0 && (
  <button
     onClick={() => setIsHamburgerOpen(true)}
    className="p-2 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-700"
  >
    ☰
  </button>
)}
                   
                <Drawer
  anchor="left"
  open={isHamburgerOpen}
  onClose={() => setIsHamburgerOpen(false)}
  ModalProps={{
    keepMounted: true, // improves performance
    BackdropProps: {
      style: {
        backgroundColor: "rgba(0, 0, 0, 0.5)", // dim/overlay the page
        backdropFilter: "blur(2px)", // optional: blur the background
      },
    },
  }}
  sx={{
    "& .MuiDrawer-paper": {
      width: "250px",
      backgroundColor: "var(--drawer-bg, white)", // make sure drawer itself is opaque
    },
  }}
>
  <div className="p-4 h-full bg-white dark:bg-gray-900">
    <h2 className="text-lg font-semibold mb-4 text-gray-900 dark:text-gray-100">
      MENUS
    </h2>
    <div className="flex flex-col gap-2">
      {menus
        .filter((menu: any) => menu?.categories?.length > 0)
        .map((menu: any) => (
          <div
            key={menu.id}
            onClick={() => {
              setSelectedMenu(menu);
              const menuCategories = menu?.categories || [];
              
              // Sort items in each category when switching menus
              const categoriesWithSortedItems = menuCategories.map((category: any) => ({
                ...category,
                items: category.items ? sortItemsAlphabetically(category.items) : []
              }));
              
              setCategories(categoriesWithSortedItems);
              setSelectedCategoryId(categoriesWithSortedItems?.[0]?.id || null);
              setIsHamburgerOpen(false);
              scrollContainerRef.current?.scrollTo({ top: 0 });
            }}
            className={`cursor-pointer px-2 py-1 rounded hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-900 dark:text-gray-100 ${
              selectedMenu?.id === menu.id ? "font-bold text-blue-500" : ""
            }`}
          >
            {menu.menuName}
          </div>
        ))}
    </div>
  </div>
</Drawer>



                          {visibleCategories.map((category) => (
                            <div className="mx-3" key={category.id}>
                              <div
                                ref={(el) => {
                                  tabRefs.current[category.id] = el;
                                }}
                                onClick={() => scrollToCategory(category.id)}
                                className={`inline-block cursor-pointer transition-colors duration-300 px-2 py-1 rounded ${selectedCategoryId === category.id
                                  ? "border-b-2 border-black text-black font-bold dark:text-gray-100 hover:bg-gray-200 dark:hover:text-black"
                                  : "text-neutral-700 font-semibold hover:bg-gray-200 dark:text-gray-100 dark:hover:text-black"
                                  }`}
                              >
                                 
                                {category.name}
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                  </div>

                  {visibleCategories.map((category) => (
                    <div
                      key={category.id}
                      ref={(el) => {
                        sectionRefs.current[category.id] = el;
                      }}
                    >
                      <h2 className="text-xl font-bold text-neutral-900 dark:text-neutral-100 mb-4">
                        {category.name}
                      </h2>
                      <div className="grid gap-4 sm:gap-8 grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5">
                    {((category.items || []) as any[]).map((item: any, index: number) => (
                      <div key={item.id ?? index} className="nc-ProductCard relative flex flex-col bg-transparent">
                        <div className="flex flex-col items-center md:items-start">
                          <div className="bg-[#ddd] h-[130px] md:h-[224px] w-[130px] md:w-[224px] rounded-3xl flex items-center justify-center mx-auto mb-3">
                            <div className="group h-full w-full relative cursor-pointer">
                              <NcImage
                                src={item.imageUrl || ""}
                                className="object-cover w-full h-full drop-shadow-lg rounded-3xl transform transition-transform duration-300 group-hover:scale-110"
                                containerClassName="flex aspect-w-1 aspect-h-1 w-full h-10"
                                onClick={() => handlePopup(item)}
                              />
                            </div>
                          </div>
                          <div className="space-y-1 px-2.5 pt-2 w-full">
                            <div className="text-center">
                              <div
                                className="font-semibold text-sm pt-1 truncate w-full px-2"
                                title={item.name}
                              >
                                {item.name}
                              </div>
                              <div className="text-xs font-semibold pt-0">
                                <div className="flex justify-center items-center">
                                  <div className="flex flex-col items-center py-1 px-4 md:px-2.5 text-sm font-medium">
                                    <span>${item.price?.toFixed(2)}</span>
                                  </div>
                                </div>
                              </div>
                            </div>
                          </div>

                        </div>
                      </div>
                    ))}
                      </div>
                    </div>
                  ))}

                  <div className="mt-4">
                    <p className="text-sm text-black font-bold text-left dark:text-white">
                      <span className="text-red-500">*</span> All prices displayed are cash prices
                    </p>
                  </div>

                </div>
              </div>
            </div>
          </div>

          {isopen && (
            <div className="fixed inset-0 flex items-center justify-center z-50 bg-black bg-opacity-50 ">
              <div className="bg-white p-6 rounded-lg shadow-lg max-w-lg w-full mx-4 sm:mx-8 max-h-[90vh] overflow-y-auto relative dark:bg-neutral-700">
                {/* Close Button */}
                <button
                  className="absolute top-3 right-3 text-gray-800 dark:text-white"
                  onClick={() => setIsopen(false)}
                >
                  &times;
                </button>

                <div className="flex">
                  {/* Image */}
                  <NcImage
                    src={popUpContent?.imageUrl}
                    className="w-[130px] h-32 object-cover mb-2 "
                    alt={popUpContent?.name}
                  />
                  <div>
                    <h2 className="text-lg sm:text-lg font-semibold ml-4 break-words max-w-[250px]">
                      {popUpContent?.name}
                    </h2>
                    <p className="py-1 px-2 md:py-1.5 md:px-3 text-md font-semibold">
                      ${popUpContent?.price.toFixed(2)}
                    </p>
                    {popUpContent?.description &&
                      popUpContent.description !== "Null" && (
                        <p className="py-1 px-2 md:py-1.5 md:px-3 text-md font-semibold">
                          {popUpContent.description}
                        </p>
                      )}
                  </div>
                </div>

                {modifierSetsContent()}
              </div>
            </div>
          )}
          {selectedSchedule && (
            <MenuScheduleFilter
              isOpen={modalOpen}
              onClose={() => setModalOpen(false)}
              menuschedule={selectedSchedule || []}
            />
          )}
        </>
      )}
    </>
  );
};

export default QRMenu;
